// patched version of "@dfb/components/spinner/index"

import {ClassName, Event, LoopSettings, Selector, States} from '@dfb/components/spinner/constants';
import BaseComponent from '@dfb/shared/base/component';
import bezierEasing from 'bezier-easing';
import {isNil} from 'lodash';

// -----------------------------------------------------------------------------
// Class Definition
// -----------------------------------------------------------------------------

export default class Spinner extends BaseComponent {
    static attachTo(root) {
        return new Spinner(root);
    }

    get mutationObserverConfig() {
        return {
            attributes: true,
            childList: true,
            characterData: true,
            attributeFilter: ['data-progress']
        };
    }

    get spinnerSize() {
        return 64;
    }

    get spinnerRadius() {
        return 30;
    }

    get easing() {
        return bezierEasing(0.16, 0, 0.12, 1);
    }

    get easingDasharray() {
        return bezierEasing(0.12, 1, 1, 0.84);
    }

    init() {
        // Check if continuous spinner
        this.continuousSpinner = this.root.classList.contains(
            ClassName.ROOT_CONTINUOUS
        );
        // Check if checkmark Spinner
        this.checkmarkSpinner = this.root.classList.contains(
            ClassName.ROOT_CHECKMARK
        );
        // Check if default Spinner
        if (!this.checkmarkSpinner && !this.continuousSpinner) {
            this.defaultSpinner = true;
        }
        // Check if value Element ist in DOM
        const valueElement = this.root.querySelector(Selector.VALUE);
        if (valueElement) {
            this.valueElement = valueElement;
        }

        // Create Mutation Observer for checkmark and default Spinner
        if (!this.continuousSpinner) {
            this.mutationObserver = this._createMutationObserver();
            this.mutationObserver.observe(this.root, this.mutationObserverConfig);
        }

        if (this.continuousSpinner) {
            this.root.classList.add(States.PENDING);
        }

        // Create Spinner SVG
        this._createSpinnerElement();

        // Create Checkmark SVG
        if (this.checkmarkSpinner) {
            this._createSvgCheckMark();
            if (this.checkmarkSvgElement) {
                this.checkmarkPathElement.style.strokeOpacity = 0;
            }
        }

        // Start Simulation
        if (this.svgSpinnerPathProgress) {
            this.svgSpinnerPathProgress.style.strokeOpacity = 0;
        }
    }

    /**
     * startet die Simulation
     */
    start() {
        if (this.defaultSpinner || this.checkmarkSpinner) {
            this._load();
        }
    }

    /**
     * Oberserver für Änderungen im DOM for default spinner und checkmark spinner
     * @param {*} mutations
     */
    _mutationsObserve(mutations) {
        mutations.forEach(mutation => {
            if (mutation.type === 'attributes') {
                if (mutation.target && mutation.target.dataset) {
                    this.progress = Math.floor(mutation.target.dataset.progress);
                    const pathLength = this._getCircleLength(this.svgSpinnerPathProgress);
                    // Update Value Element only by default spinner
                    if (this.valueElement && this.defaultSpinner) {
                        this.updateValueElement();
                    }

                    // Animation
                    if (this.progress === 100) {
                        this.svgSpinnerPathProgress.style.strokeDasharray = 'none';

                        if (this.checkmarkSpinner) {
                            this._animateCheckmark();
                        }
                    } else {
                        const dashArrayValue = (this.progress * pathLength) / 100;
                        this.svgSpinnerPathProgress.style.strokeDasharray = `${dashArrayValue}, ${pathLength}`;
                    }

                    if (this.progress === 0) {
                        this.svgSpinnerPathProgress.style.strokeOpacity = 0;
                    } else {
                        this.svgSpinnerPathProgress.style.strokeOpacity = 1;
                    }

                    this.updateStateClasses();
                    this._emitEvents();
                }
            }
        });
    }

    /**
     * Aktualisiert das Value element mit dem aktuellen progress wert
     */
    updateValueElement() {
        if (!isNil(this.progress)) {
            this.valueElement.innerHTML = this.progress;
        }
    }

    /**
     * Führt den continuous spinner aus
     */
    loop() {
        if (this.continuousSpinner) {
            this.svgSpinnerPathProgress.style.strokeOpacity = 1;
            window.requestAnimationFrame(timestamp => this._animateLoop(timestamp));
        }
    }

    /**
     * animiert den continuous spinner
     * @param {*} timestamp
     */
    _animateLoop(timestamp) {
        if (!this._loopStart) {
            this._loopStart = timestamp;
        }

        this.root.classList.remove(States.HIDDEN);
        this.root.classList.remove(States.PENDING);
        this.root.classList.add(States.LOADING);

        // The progress in seconds 0 <= progress <= LOOP_ANIMATION_DURATION
        let progress = (timestamp - this._loopStart) / 1000;
        // The progress ratio between 0 <= progressRatio <= 1
        let progressRatio = progress / LoopSettings.DURATION;
        if (progressRatio > 1 || progressRatio === 0) {
            this._animationEndSet = false;
            progress = 0;
            progressRatio = 0;
            this._loopStart = timestamp;
            this._dashLengthStart = LoopSettings.ANIMATION[0].dashLength;
            this._dashOffsetStart = LoopSettings.ANIMATION[0].dashOffset;
            this._dashLengthEnd = LoopSettings.ANIMATION[1].dashLength;
            this._dashOffsetEnd = LoopSettings.ANIMATION[1].dashOffset;
        }
        const ease = this.easing(progressRatio);
        const pathLength = this._getCircleLength(this.svgSpinnerPathProgress);
        this._dashLengthRatio =
            this._dashLengthStart +
            (this._dashLengthEnd - this._dashLengthStart) *
            this.easingDasharray(progressRatio);
        this._dashOffsetRatio =
            this._dashOffsetStart +
            (this._dashOffsetEnd - this._dashOffsetStart) * ease;
        this._rotate = LoopSettings.ANIMATION[1].rotate * ease;

        const dashLength = pathLength * this._dashLengthRatio;
        const dashOffset = pathLength * this._dashOffsetRatio;

        this.svgSpinnerPathProgress.style.strokeDasharray = `${dashLength}, ${pathLength}`;
        this.svgSpinnerPathProgress.style.strokeDashoffset = `${dashOffset}`;
        // this._group.setAttributeNS(null, 'transform', `rotate(${this._rotate})`);
        this.svgSpinnerGroupProgress.setAttributeNS(
            null,
            'transform',
            `rotate(${this._rotate})`
        );

        this.loopAnimation = window.requestAnimationFrame(timestamp =>
            this._animateLoop(timestamp)
        );
    }

    /**
     * stoppt den continuous spinner
     */
    stop() {
        this._loopStart = false;
        window.cancelAnimationFrame(this.loopAnimation);
        this.svgSpinnerPathProgress.style.strokeOpacity = 0;
        this.root.classList.remove(States.LOADING);
        this.root.classList.add(States.COMPLETE);
        this.root.classList.add(States.HIDDEN);
    }

    /**
     * Animiert die Checkmark if spinner is complete
     */
    _animateCheckmark() {
        this.checkmarkPathElement.style.strokeOpacity = 1;
    }

    /**
     * Aktualisiert die Status-Klassen
     */
    updateStateClasses() {
        if (this.progress === 0) {
            this.root.classList.add(States.PENDING);
        } else {
            this.root.classList.remove(States.PENDING);
        }

        if (this.progress > 0 && this.progress < 100) {
            this.root.classList.add(States.LOADING);
        } else {
            this.root.classList.remove(States.LOADING);
        }

        if (this.progress === 100) {
            this.root.classList.add(States.COMPLETE);
        } else {
            this.root.classList.remove(States.COMPLETE);
        }
    }

    /**
     * Create Events
     */
    _emitEvents() {
        if (this.progress === 0) {
            this.emit(Event.PENDING, {
                progessValue: this.progress,
                progressElement: this.root
            });
        }

        if (this.progress === 1) {
            this.emit(Event.STARTED, {
                progessValue: this.progress,
                progressElement: this.root
            });
        }

        if (this.progress > 0 && this.progress < 100) {
            this.emit(Event.LOADING, {
                progessValue: this.progress,
                progressElement: this.root
            });
        }

        if (this.progress === 100) {
            this.emit(Event.COMPLETE, {
                progessValue: this.progress,
                progressElement: this.root
            });
        }

        if (this.root.classList.contains(States.HIDDEN)) {
            this.emit(Event.HIDDEN, {
                progessValue: this.progress,
                progressState: 'hidden',
                progressElement: this.root
            });
        }
    }

    /**
     * Errechnet die Circle Länge
     * @param {*} circle
     */
    _getCircleLength(circle) {
        const circleRadius = circle.getAttribute('r');
        return 2 * Math.PI * circleRadius;
    }

    _getPolylineLength(polyline) {
        let totalLength = 0;
        let prevPos = {
            x: 0,
            y: 0
        };

        for (let i = 0; i < polyline.points.numberOfItems; i++) {
            const pos = polyline.points.getItem(i);
            if (i > 0) {
                totalLength += Math.sqrt(
                    Math.pow(pos.x - prevPos.x, 2) + Math.pow(pos.y - prevPos.y, 2)
                );
            }
            prevPos = pos;
        }
        return totalLength;
    }

    /**
     * Create SVG Spinner Element with sub childs
     */
    _createSpinnerElement() {
        const shape = this._createSvgNode('svg', {
            class: ClassName.SVG,
            viewBox: `-${this.spinnerSize / 2} -${this.spinnerSize / 2}
        ${this.spinnerSize} ${this.spinnerSize}`
        });
        const circleGroup = this._createSVGGroupNode('g', ClassName.GROUP);
        const circleGroupBase = this._createSVGGroupNode('g', ClassName.GROUPBASE);
        const circleGroupProgress = this._createSVGGroupNode(
            'g',
            ClassName.GROUPPROGRESS
        );
        const circleBase = this._createSVGCircleNode(
            'circle',
            ClassName.PATH,
            0,
            0,
            this.spinnerRadius
        );
        const circleProgress = this._createSVGCircleNode(
            'circle',
            ClassName.PATHPROGRESS,
            0,
            0,
            this.spinnerRadius
        );

        if (
            shape &&
            circleGroup &&
            circleGroupBase &&
            circleGroupProgress &&
            circleBase &&
            circleProgress
        ) {
            circleGroupBase.appendChild(circleBase);
            circleGroupProgress.appendChild(circleProgress);
            circleGroup.appendChild(circleGroupBase);
            circleGroup.appendChild(circleGroupProgress);
            shape.appendChild(circleGroup);
            this.root.appendChild(shape);

            this.svgSpinnerElement = shape;
            this.svgSpinnerGroup = circleGroup;
            this.svgSpinnerPathProgress = circleProgress;
            this.svgSpinnerPathProgress = circleProgress;
            this.svgSpinnerPathBase = circleBase;
            this.svgSpinnerGroupProgress = circleGroupProgress;
        }
    }

    /**
     * Create SVG Group Element
     * @param {} svgElement
     * @param {*} svgClass
     */
    _createSVGGroupNode(svgElement, svgClass) {
        const circleGroup = this._createSvgNode(svgElement, {
            class: svgClass
        });
        if (circleGroup) {
            return circleGroup;
        } else {
            return undefined;
        }
    }

    /**
     * Create SVG Circle Element
     * @param {} svgElement
     * @param {*} svgClass
     * @param {*} svgCx
     * @param {*} svgCy
     * @param {*} svgRadius
     */
    _createSVGCircleNode(svgElement, svgClass, svgCx, svgCy, svgRadius) {
        const circle = this._createSvgNode(svgElement, {
            class: svgClass,
            cx: svgCx,
            cy: svgCy,
            r: svgRadius
        });
        if (circle) {
            return circle;
        } else {
            return undefined;
        }
    }

    /**
     * Create SVG Node Element
     */
    _createSvgNode(qualifiedName, attributes) {
        const element = document.createElementNS(
            'http://www.w3.org/2000/svg',
            qualifiedName
        );
        for (var attr in attributes) {
            element.setAttributeNS(null, attr, attributes[attr]);
        }
        return element;
    }

    _createSvgCheckMark() {
        const checkmarkSvg = this._createSvgNode('svg', {
            class: ClassName.SVGCHECKMARK,
            viewBox: ` 0 0 24 24`
        });

        const checkmarkPath = this._createSvgNode('polyline', {
            class: ClassName.PATHCHECKMARK,
            points: '1.3,11.8 8.6,19.1 22.7,3.4 '
        });

        if (checkmarkSvg && checkmarkPath) {
            checkmarkSvg.appendChild(checkmarkPath);
            this.root.appendChild(checkmarkSvg);

            this.checkmarkSvgElement = checkmarkSvg;
            this.checkmarkPathElement = checkmarkPath;
        }
    }

    /**
     * Create an MutationObserver
     */
    _createMutationObserver() {
        const observer = new MutationObserver(
            (this._mutationObserveHandler = mutations =>
                this._mutationsObserve(mutations))
        );
        return observer;
    }

    /**
     * Create a simulation of the progress value and updates the data-progress-Attribute on the root element
     */
    _load() {
        const time = Math.floor(Math.random() * 150);
        if (this.root) {
            let value = 0;
            const progress = setInterval(() => {
                value = value + 1;
                this.root.setAttribute('data-progress', value);

                if (value >= 100) {
                    clearInterval(progress);
                }
            }, time);
        }
    }

    /**
     * Zerstört die Instance des Spinners
     */
    destroy() {
        if (this.root && this.svgSpinnerElement) {
            this.root.removeChild(this.svgSpinnerElement);
        }

        this.stop();
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    }
}
